import React, { useState, useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { store } from './store';
import { ThemeProvider } from './themes/ThemeProvider';
import BrandingHead from './themes/BrandingHead';
import Layout from './components/layout/Layout';
import ScrollToTop from './components/routing/ScrollToTop';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import Checkout from './components/Checkout';
import CheckoutConfirm from './components/CheckoutConfirm';
import CategoryPage from './pages/CategoryPage';
import OrderConfirmation from './components/OrderConfirmation';
import ErrorBoundary from './components/ErrorBoundary';
import { fetchRuntimeConfig } from './services/config';
// Side-effect import: kicks off i18next bootstrap once runtime config is
// known. See src/i18n/index.ts (issue #140).
import './i18n';

const App: React.FC = () => {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    fetchRuntimeConfig()
      .then(() => {
        console.log('[App] Runtime config loaded successfully');
        setConfigLoaded(true);
      })
      .catch((error) => {
        console.error('[App] Failed to load runtime config:', error);
        setConfigError(error.message);
      });
  }, []);

  if (configError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Configuration Error</h1>
        <p>{configError}</p>
        <p>Please check your runtime configuration.</p>
      </div>
    );
  }

  if (!configLoaded) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading configuration...</p>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrandingHead />
        <Provider store={store}>
          <Router>
            <ScrollToTop />
            <Layout>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/products/:productId" element={<ProductDetailPage />} />
                <Route path="/categories/:handle" element={<CategoryPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/checkout/confirm" element={<CheckoutConfirm />} />
                <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
              </Routes>
            </Layout>
          </Router>
        </Provider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
